// Classifies a staff role string into FOH (Front of House), BOH (Back of
// House), or Other. Restaurants rarely store a formal `department` field on
// their people, so we infer it from the free-text role that the chef
// already types when adding a team member (e.g. "Line Cook", "Server",
// "Bartender"). Classification is case-insensitive and matches the first
// keyword that hits, so specific words win over broader ones.
//
// If more precision is ever needed, a `department` field can be added to
// the staff schema and this helper can prefer that value when present.

const FOH_KEYWORDS = [
  "server", "waiter", "waitress", "host", "hostess",
  "bartender", "barback", "bar back", "sommelier",
  "runner", "food runner", "busser", "bus ",
  "front", "foh", "maitre", "captain",
  "cashier", "greeter", "concierge",
];

const BOH_KEYWORDS = [
  "chef", "cook", "kitchen", "boh",
  "prep", "dish", "dishwasher", "steward",
  "expo", "expeditor", "pastry", "baker",
  "sous", "garde", "saucier", "butcher",
  "line", "pass ", "grill", "fry", "pantry",
  "back",
];

export function departmentForRole(role) {
  if (!role) return "Other";
  const r = String(role).toLowerCase();
  for (const kw of BOH_KEYWORDS) {
    if (r.includes(kw)) return "BOH";
  }
  for (const kw of FOH_KEYWORDS) {
    if (r.includes(kw)) return "FOH";
  }
  return "Other";
}

export function departmentLabel(code) {
  if (code === "FOH") return "Front of House";
  if (code === "BOH") return "Back of House";
  return "Other";
}

// Groups shifts by inferred department. Returns an object keyed by FOH /
// BOH / Other, each carrying hours, cost, shift count, and staff count.
// `staffById` map is required to look up role and rate for each shift.
export function groupShiftsByDepartment(shifts, staffById, computeShiftCost) {
  const buckets = {
    FOH: { hours: 0, cost: 0, shifts: 0, staffIds: new Set() },
    BOH: { hours: 0, cost: 0, shifts: 0, staffIds: new Set() },
    Other: { hours: 0, cost: 0, shifts: 0, staffIds: new Set() },
  };
  for (const sh of shifts) {
    const staff = staffById[sh.staff_id];
    const dep = departmentForRole(staff?.role);
    const c = computeShiftCost(sh, staffById);
    buckets[dep].hours += c.hours;
    buckets[dep].cost += c.cost;
    buckets[dep].shifts += 1;
    if (sh.staff_id) buckets[dep].staffIds.add(sh.staff_id);
  }
  return buckets;
}
